import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { type WorkflowStep, WorkflowStepUserResponses } from "~/api/types";
import { moveWorkflow } from "~/api/v2/requests";
import i18n from "~/i18n";

export interface WorkflowTransitionConfirmMessage {
  title: string;
  description: string;
}

const ConfirmMessage = {
  someRejected: {
    title: i18n.t(($) => $.commons.workflow.proceedWithRejectedResponses),
    description: i18n.t(($) => $.commons.workflow.someParticipantsRejected),
  },
  notAllResponded: {
    title: i18n.t(($) => $.commons.workflow.proceedWithoutAllResponses),
    description: i18n.t(($) => $.commons.workflow.notAllResponded),
  },
} as const;

export function useWorkflowStepTransition({
  documentArtifactId,
  onChange,
}: {
  documentArtifactId: number;
  onChange: () => void;
}) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmMessage, setConfirmMessage] =
    useState<WorkflowTransitionConfirmMessage | null>(null);

  const transition = async (direction: "backward" | "forward") => {
    try {
      setIsSubmitting(true);
      await moveWorkflow(documentArtifactId, direction);
      onChange();
    } catch {
      toast.error(t(($) => $.toasts.error.workflowNotMoved));
    } finally {
      setIsSubmitting(false);
    }
  };

  const requestNextStep = async (step: WorkflowStep) => {
    const allUsersResponded = step.users.every(
      (u) => u.responseStatus !== WorkflowStepUserResponses.pending,
    );
    if (!allUsersResponded) {
      setConfirmMessage(ConfirmMessage.notAllResponded);
      return;
    }

    const someUserRejected = step.users.some(
      (u) => u.responseStatus === WorkflowStepUserResponses.rejected,
    );
    if (someUserRejected) {
      setConfirmMessage(ConfirmMessage.someRejected);
      return;
    }

    await transition("forward");
  };

  return {
    isSubmitting,
    confirmMessage,
    requestNextStep,
    moveForward: () => transition("forward"),
    moveBackward: () => transition("backward"),
    dismissConfirm: () => setConfirmMessage(null),
  };
}
